import React from "react";

import cx from "classnames";

import {
  coalesce,
  doNothing,
  maybeGetClosureFor,
  getWithDefault,
} from "@kazoohr/helpers";

// Default function for onPageChange when not provided

import { useWindowSize } from "../../helpers/useWindowSize";
import { Checkbox } from "../Checkbox";
import { Flex } from "../Flex";
import { IconButton } from "../IconButton";
import { Loader } from "../Loader";
import { Pagination } from "../Pagination/Pagination";

import styles from "./DataTable.scss";
import { MobileTableRow } from "./MobileTableRow";
import { NestedRowsGenerator } from "./NestedRowsGenerator";
import { NestedTableRow } from "./NestedTableRow";
import { TableHeader } from "./TableHeader";
import { TableRow } from "./TableRow";
import { breakpoints } from "./types";
import type { Alignment, TableColumn, DataKey, TableDataType } from "./types";

/**
 * Base props interface for the DataTable component
 */
export interface DataTableBaseProps<
  DataType extends TableDataType = TableDataType,
  DataIdType extends DataKey = DataKey
> {
  /**
   * Sets the table text alignment
   */
  alignment?: Alignment;
  /**
   * If true, highlights every other row
   */
  alternateRows?: boolean;
  /**
   * If true, removes padding from the table
   */
  noPadding?: boolean;
  /**
   * JSX element that will be rendered below the table
   */
  bottomComponent?: React.ReactNode;
  /**
   * JSX element that will be rendered in the bulk action bar part of the table
   */
  bulkActionBar?: React.ReactNode;
  /**
   * Applies CSS class name to the wrapper / container of the table
   */
  className?: string;
  /**
   * List of column config objects
   */
  columns?: TableColumn[];
  /**
   * Current page number
   */
  currentPage?: number;
  /**
   * Data set
   */
  data?: DataType[];
  /**
   * Strategy for creating a unique key for each row
   */
  dataKey: (dataItem: DataType) => DataIdType;
  /**
   * A list of row ids for disabling their checkbox
   */
  disabledRowIds?: DataIdType[];
  /**
   * Sets whether nested rows feature should be enabled or not
   */
  enableNestedRows?: boolean;
  /**
   * A list of row ids that have been expanded
   */
  expandedRowIds?: DataIdType[];
  /**
   * A component that will be used as a template inside an Expanded Row
   */
  expandedRowTemplateComponent?: (dataItem: DataType) => React.ReactNode;
  /**
   * If true, we don't know the total number of pages, so we change the UI to represent that
   */
  foggyPagination?: boolean;
  /**
   * Defines whether all rows (and the `select all` checkbox) are checked or not
   */
  isSelectAllChecked?: boolean;
  /**
   * Sets whether the table is in a loading state or not
   */
  loading?: boolean;
  /**
   * Header displayed when responsive layout is shown
   */
  mobileHeader?: React.ReactNode;
  /**
   * Object Property that has nested data on its value
   */
  nestedField?: string;
  /**
   * Callback function triggered when a column header is clicked, typically used to handle column sorting
   */
  onClick?: (clickedColumnHeaderName: string) => void;
  /**
   * When the user changes the pagination page, we call this function
   */
  onPageChange?: (page: number) => void;
  /**
   * When the user changes the pagination size, we call this function
   */
  onPaginationSizeChange?: (size: string) => void;
  /**
   * A function that alters state in the parent component for whether the specified row is expanded
   */
  onToggleExpansionOne?: (id: DataIdType) => void;
  /**
   * A function that alters state in the parent component for whether all rows are selected or not
   */
  onToggleSelectAll?: (ids: DataIdType[]) => void;
  /**
   * A function that alters state in the parent component for whether the specified row is selected
   */
  onToggleSelectOne?: (id: DataIdType) => void;
  /**
   * Text that will be displayed next to the pagination select input
   */
  paginationLabelLeft?: string;
  /**
   * Text that will be displayed before the pagination controls
   */
  paginationLabelRight?: string;
  /**
   * Available pagination options to the user
   */
  paginationOptions?: Array<{ text: string; value: string }>;
  /**
   * Size of the page. It's a string for historical reasons.
   */
  paginationSize?: string;
  /**
   * Sets responsive breakpoint when `enableResponsiveLayout` is `true`
   */
  responsiveBreakpoint?: keyof typeof breakpoints;
  /**
   * This is only for nested rows. This is the status of the checkbox located at thead
   */
  selectAllStatus?: "checked" | "unchecked" | "half-checked";
  /**
   * A list of row ids that have been selected (checkboxes are marked)
   */
  selectedRowIds?: DataKey[];
  /**
   * Sets whether the checkbox column is visible and if rows can be selected
   */
  showCheckboxes?: boolean;
  /**
   * Sets whether the expansion toggle column is visible and if rows can be expanded
   */
  showExpansionToggles?: boolean;
  /**
   * JSX element that will be rendered above the table
   */
  topComponent?: React.ReactNode;
  /**
   * Total number of pages
   */
  totalPages?: number;
  /**
   * Sets whether pagination is shown or not
   */
  withoutPagination?: boolean;
  /**
   * Sets whether the table should have shadows
   */
  withoutShadows?: boolean;
  /**
   * Sets whether responsive layout should be used in low resolutions
   */
  withResponsiveLayout?: boolean;
  /**
   * If true, the pagination will be displayed at the top of the table. By default, renders at the bottom
   */
  withTopPagination?: boolean;
  /**
   * JSX element that will be rendered when there is no data
   */
  zeroState?: React.ReactNode;
}

export type DataTableProps<
  DataType extends TableDataType = TableDataType,
  DataIdType extends DataKey = DataKey
> = DataTableBaseProps<DataType, DataIdType>;

/**
 * A Data Table component that organizes data within a responsively contained table.
 * Features include:
 * - Responsive layout for mobile devices (screen resolution < 601px)
 * - Pagination controls
 * - Row selection
 * - Row expansion
 * - Nested rows
 * - Sorting
 * - Custom cell rendering
 *
 * @template DataType - The type of data being displayed in the table
 * @template DataIdType - The type of the unique identifier for each row
 */
// eslint-disable-next-line max-lines-per-function
export function DataTable<
  DataType extends TableDataType = TableDataType,
  DataIdType extends DataKey = DataKey
>({
  alignment = "left",
  columns = [],
  data = [],
  dataKey,
  showCheckboxes = false,
  showExpansionToggles = false,
  selectedRowIds = [],
  onClick = doNothing,
  onToggleExpansionOne,
  onToggleSelectAll,
  onToggleSelectOne,
  isSelectAllChecked,
  expandedRowTemplateComponent,
  expandedRowIds = [],
  className,
  withoutShadows,
  alternateRows,
  mobileHeader,
  withoutPagination = true,
  loading = false,
  paginationOptions = [],
  paginationSize,
  onPageChange,
  onPaginationSizeChange,
  currentPage = 1,
  totalPages = 1,
  enableNestedRows = false,
  nestedField = "children",
  withResponsiveLayout = false,
  selectAllStatus,
  responsiveBreakpoint = 600,
  bottomComponent,
  topComponent,
  bulkActionBar,
  paginationLabelLeft,
  paginationLabelRight,
  zeroState = null,
  disabledRowIds = [],
  withTopPagination = false,
  foggyPagination = false,
  noPadding = false,
  ...rest
}: DataTableProps<DataType, DataIdType>) {
  const { width } = useWindowSize();
  const showResponsiveLayout =
    withResponsiveLayout && coalesce(width, 0) <= responsiveBreakpoint;

  const tableClassNames = cx(styles.table, styles[`align_${alignment}`]);

  const overflowClassNames = cx(styles.overflow, {
    [styles["overflow-mobile"]]: showResponsiveLayout,
    [styles["overflow-loading"]]: loading,
  });

  const tableBodyClassNames = cx(styles["table_body"], {
    [styles["alternate_rows"]]: alternateRows,
  });

  const checkboxColumn: TableColumn = {
    isCheckBox: true,
    header: "checkbox",
    renderCell: (dataItem) => {
      const isDisabled = disabledRowIds.includes(dataItem.id);
      const isChecked = selectedRowIds.includes(dataItem.id);
      return (
        <Checkbox
          id={dataItem.id}
          checked={isChecked}
          onChange={maybeGetClosureFor(onToggleSelectOne, dataItem.id)}
          disabled={isDisabled}
        />
      );
    },
  };

  const expansionToggleColumn: TableColumn = {
    isCheckBox: false,
    isExpansionToggle: true,
    header: "",
    renderCell: (dataItem) => {
      const { id } = dataItem;
      const isExpanded = expandedRowIds.includes(id);
      const icon = isExpanded ? "caret_up" : "caret_dn";
      return (
        <IconButton
          className="expansion-toggle"
          icon={icon}
          data-testid={isExpanded ? "expanded" : "unexpanded"}
          size="extra_small"
          variant="transparent"
          onClick={maybeGetClosureFor(onToggleExpansionOne, id)}
        />
      );
    },
  };

  let adjustedColumns = columns;
  if (showCheckboxes && showExpansionToggles) {
    // Adds checkboxes and expansion toggles to the first two columns of the table
    adjustedColumns = [checkboxColumn, expansionToggleColumn, ...columns];
  } else if (showCheckboxes) {
    // Adds checkboxes to the first column of the table
    adjustedColumns = [checkboxColumn, ...columns];
  } else if (showExpansionToggles) {
    // Adds expansion toggles to the first column of the table
    adjustedColumns = [expansionToggleColumn, ...columns];
  }

  const containerStyles = cx(
    styles.container,
    {
      [styles["container-loading"]]: loading,
      [styles.withoutShadows]: withoutShadows,
      [styles["container-responsive"]]: showResponsiveLayout,
      [styles["container-no-padding"]]: noPadding,
    },
    className
  );

  function getCheckState(
    item: DataType
  ): "checked" | "half-checked" | "unchecked" {
    const id = dataKey(item);
    if ((item[nestedField] || []).length) {
      const childrenState: ("checked" | "half-checked" | "unchecked")[] = item[
        nestedField
      ]?.map((child: DataType) => getCheckState(child));
      if (childrenState.every((child) => child === "checked")) {
        return "checked";
      } else if (childrenState.every((child) => child === "unchecked")) {
        return "unchecked";
      } else {
        return "half-checked";
      }
    }
    return selectedRowIds.includes(id) ? "checked" : "unchecked";
  }

  function getChildrenIds(dataItem: DataType): DataIdType[] {
    const id = dataKey(dataItem);
    const childrenIds = coalesce(dataItem[nestedField], []).reduce(
      (prev: string, current: DataType) => {
        if (current[nestedField]) {
          return [...getChildrenIds(current), ...prev];
        }
        return [dataKey(current), ...prev];
      },
      []
    );
    return [id, ...childrenIds];
  }

  const displayRows = (data && data.length > 0) || !zeroState;
  const current =
    foggyPagination || (currentPage > 0 && data.length > 0) ? currentPage : 1;
  const total =
    foggyPagination || (totalPages > 0 && data.length > 0) ? totalPages : 1;

  function renderPaginationSection(
    testId: string,
    showQty = true,
    className = ""
  ) {
    return (
      <Pagination
        onPage={getWithDefault(onPageChange, doNothing)}
        current={current}
        total={total}
        pageCountOptions={paginationOptions.map((option) => ({
          label: option.text,
          value: option.value,
        }))}
        selectedPageQuantity={paginationSize}
        onPageCountChange={onPaginationSizeChange}
        paginationLabelLeft={paginationLabelLeft}
        paginationLabelRight={paginationLabelRight}
        foggyPagination={foggyPagination}
        className={className}
        showPageCount={showQty}
        data-testid={testId}
      />
    );
  }

  return (
    <div className={containerStyles}>
      {loading ? (
        <div className={styles.loader} data-testid="data-table-loader">
          <Loader />
        </div>
      ) : null}
      <div className={overflowClassNames}>
        <Flex>
          <div className={styles["top-component"]}>{topComponent}</div>
          {!withoutPagination &&
            withTopPagination &&
            renderPaginationSection(
              "top-pagination",
              false,
              styles["top-pagination"]
            )}
        </Flex>
        {bulkActionBar}
        {!showResponsiveLayout && (
          <>
            <table className={tableClassNames} {...rest}>
              <thead>
                <TableHeader
                  columns={adjustedColumns}
                  isSelectAllChecked={isSelectAllChecked}
                  onToggleSelectAll={maybeGetClosureFor(
                    onToggleSelectAll,
                    data.map(dataKey)
                  )}
                  showCheckboxes={showCheckboxes}
                  enableNestedRows={enableNestedRows}
                  defaultAlignment={alignment}
                  onClick={onClick}
                  selectAllStatus={selectAllStatus}
                />
              </thead>
              {displayRows ? (
                <tbody className={tableBodyClassNames}>
                  {data.map((dataItem, index) => {
                    const id = dataKey(dataItem);

                    const expandedRowIncludesId = expandedRowIds.includes(id);
                    const isExpanded =
                      showExpansionToggles && expandedRowIncludesId;
                    if (!enableNestedRows) {
                      return (
                        <TableRow
                          key={id}
                          columns={adjustedColumns}
                          dataItem={dataItem}
                          defaultAlignment={alignment}
                          showCheckboxes={showCheckboxes}
                          isExpanded={isExpanded}
                          renderExpandedRowContent={() =>
                            isExpanded &&
                            expandedRowTemplateComponent?.(dataItem)
                          }
                          rowNumber={index}
                          dataKey={dataKey}
                          onToggleSelectOne={maybeGetClosureFor(
                            onToggleSelectOne,
                            id
                          )}
                        />
                      );
                    } else {
                      if (expandedRowIncludesId) {
                        return (
                          <NestedRowsGenerator
                            key={`generator-${id}`}
                            dataItem={dataItem}
                            nestedLevel={0}
                            expandedRowIds={expandedRowIds}
                            nestedField={nestedField}
                            getChildrenIds={getChildrenIds}
                            onToggleSelectOne={onToggleSelectOne}
                            dataKey={dataKey}
                            columns={columns}
                            alignment={alignment}
                            showCheckboxes={showCheckboxes}
                            onToggleExpansionOne={onToggleExpansionOne}
                            getCheckState={getCheckState}
                          />
                        );
                      } else {
                        return (
                          <NestedTableRow
                            key={id}
                            columns={columns}
                            dataItem={dataItem}
                            defaultAlignment={alignment}
                            nestedLevel={0}
                            onToggleSelectOne={maybeGetClosureFor(
                              onToggleSelectOne,
                              id
                            )}
                            showCheckboxes={showCheckboxes}
                            onToggleExpansionOne={onToggleExpansionOne}
                            hasChildren={Boolean(
                              dataItem?.[nestedField]?.length
                            )}
                            isExpanded={false}
                            checkState={getCheckState(dataItem)}
                            dataKey={dataKey}
                          />
                        );
                      }
                    }
                  })}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td
                      data-testid="dataTable-zeroState"
                      className={styles["zeroStateContainer"]}
                      colSpan={columns.length + (showCheckboxes ? 1 : 0)}
                    >
                      <Flex justifyContent="center" alignItems="center">
                        <div>{zeroState}</div>
                      </Flex>
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
            {!withoutPagination && renderPaginationSection("bottom-pagination")}
          </>
        )}
        {bottomComponent}
      </div>
      {showResponsiveLayout && (
        <div
          className={styles["mobile-table-wrapper"]}
          data-testid="responsive-layout"
        >
          <div className={styles["mobile-table-controls"]}>{mobileHeader}</div>
          <div className={styles["mobile-table"]}>
            {displayRows ? (
              data.map((dataItem) => {
                const id = dataKey(dataItem);
                return (
                  <MobileTableRow
                    key={id}
                    columns={columns}
                    dataItem={dataItem}
                    showCheckboxes={showCheckboxes}
                    selectedRowIds={selectedRowIds}
                    onToggleSelectOne={onToggleSelectOne}
                  />
                );
              })
            ) : (
              <Flex
                className={styles["zeroStateContainer"]}
                justifyContent="center"
                alignItems="center"
              >
                <div>{zeroState}</div>
              </Flex>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
